import * as THREE from "three";
import { WaterEffect } from "../maps/watereffect";
import { UnderwaterOverlay } from "../maps/underwateroverlay";
import type { GameContext, GameMiddleware } from "./types";

/**
 * Handles underwater detection, scene fog, and the UnderwaterOverlay effect.
 *
 * In the editor this only activates in preview mode (onModeChange).
 * In the client it is always active (onModeChange is never called).
 *
 * Note: UnderwaterOverlay.render() must still be called by the host render loop
 * after scene rendering — this middleware only handles state updates.
 */
export class WaterMiddleware implements GameMiddleware {
    private ctx!: GameContext;
    // Default true so the standalone client is always active.
    // The Editor resets this to false via onModeChange("editor") after init.
    private _active = true;
    private _wasUnderwater = false;

    init(ctx: GameContext): void {
        this.ctx = ctx;
    }

    update(delta: number): void {
        if (!this._active) {
            UnderwaterOverlay.setEnabled(false);
            return;
        }

        const cam = this.ctx.getActiveCamera();
        const planes = WaterEffect.waterPlanes;
        let underwater = false;
        const camLocalX = cam.position.x - 5200;
        const camLocalY = cam.position.y - 5200;
        for (const p of planes) {
            if (
                camLocalX >= p.minX && camLocalX <= p.maxX &&
                camLocalY >= p.minY && camLocalY <= p.maxY &&
                cam.position.z < p.surfaceZ
            ) {
                underwater = true;
                break;
            }
        }

        if (underwater !== this._wasUnderwater) {
            this._wasUnderwater = underwater;
            this.ctx.scene.fog = underwater ? new THREE.FogExp2(0x003a6e, 0.06) : null;
        }

        UnderwaterOverlay.setEnabled(underwater);
        UnderwaterOverlay.update(delta);
    }

    /** True when the active camera is currently below a water surface. */
    get isUnderwater(): boolean { return this._wasUnderwater; }

    onModeChange(mode: "editor" | "preview"): void {
        this._active = mode === "preview";
        if (mode === "editor") {
            // Clean up any lingering underwater state when leaving preview
            this._wasUnderwater = false;
            this.ctx.scene.fog = null;
            UnderwaterOverlay.setEnabled(false);
        }
    }
}
